import React, { useEffect, useRef } from "react";

// Lib
import { Helmet } from "react-helmet";
import axios, { AxiosError } from "axios";
import SlideRoutes from "react-slide-routes";
import { useErrorHandler } from "react-error-boundary";
import { useLocation } from "react-router-dom";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

// Components

// Utils
import i18n, { i18nInitPromise } from "./i18n";

// Services
import useOnlineStatus from "./hooks/useOnlineStatus";

// Type Definitions
import { useApp } from "./context/AppContext";
import useManageLocalizationFailure from "./hooks/useManageLocalizationFailure";
import { LocationSelectionConfig } from "./components/StaticWidgets/LOCATION_SELECTION";
import { RecoEngineConfig } from "./features/RecoEngine";
import { SearchBarConfig } from "./components/StaticWidgets/SEARCH_BAR";
import { ProfileConfig } from "./components/StaticWidgets/PROFILE_PREFERENCES";
import { initializeNativePostMessageService } from "./services/NativePostMessage";
import RouteCreationService from "./services/RouteCreationService";
import useAssistiveFocusScroll from "./hooks/useAssistiveFocusScroll";
import useFlows from "./hooks/useFlows";
import ModalCreationService from "./services/ModalCreationService";
import useOnboardingRedirect from "./hooks/useOnboardingRedirect";
import PWAHeader from "./components/PWA/pwaHeader";
import PWAInstallPrompt from "./components/PWA/pwaInstallPrompt";
import { getHostPrefix } from "./utils/CommonFunctions";


declare global {
  interface Window {
    config: any;
    webkit: any;
    APP_COMPONENT: any;
    GLOBAL_PATH: any;
    Android: any;
    opera?: any;
    MSStream?: any;
  }
}
const App = () => {
  const {
    isAppReady,
    setIsAppReady,
    isAnalyticsInitialized,
    setIsAnalyticsInitialized,
    addConfig,
    configs,
    setSettings,
  } = useApp();
  const handleError = useErrorHandler();
  const { onlineStatus } = useOnlineStatus();
  const location = useLocation();

  // <--- Handle Errors --->

  // Axios Error Handler: register once and clean up on unmount to avoid stacking interceptors
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (res) => res,
      (error: AxiosError) =>
        handleError({
          message: error.message,
          error_code: "ERROR_API_FAILURE",
          error_response: error.response,
        })
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [handleError]);

  // Manage Localization Failure
  useManageLocalizationFailure();

  // OnlineStatus Handler
  useEffect(() => {
    if (onlineStatus === "offline") {
      sessionStorage.setItem("lastPageBeforeError", window.location.pathname);
      handleError({
        message: "Network Connection Lost",
        error_code: "ERROR_NO_NETWORK",
      });
    }
  }, [onlineStatus, handleError]);

  // Scroll focused controls into view for keyboard / assistive tech only (APS-2013)
  useAssistiveFocusScroll();

  // Ref for sidebar
  const childRef = useRef({});
  window.APP_COMPONENT = childRef;

  useEffect(() => {
    const startupActions = async () => {
      const hostPrefix = getHostPrefix();
      const headID = document.getElementsByTagName("head")[0];
      const num = `${Math.random()}`.slice(2).padEnd(20, "0").slice(0, 20);
      const rootElement = document.getElementById("root");
      try {
        // 1. Fetch config.json (Essential Step)
        const configResponse = await axios.get("./config/config.json");
        window.config = configResponse.data;
        const config = configResponse.data;


        // 2. Handle Whitelabel CSS Load (Optional/Delayed Step)
        let whitelabelUrl;
        if (config.remote_prefix) {
          whitelabelUrl = `${config.remote_prefix}/${hostPrefix}/whitelabel.css?${num}`;
        } else if (config.whitelabel) {
          whitelabelUrl = config.whitelabel;
        }

        if (whitelabelUrl) {
          // Inject Specificity Wrapper Rule
          const style = document.createElement("style");
          style.type = "text/css";
          const specificityWrapper = "#root.whitelabel-active { content: ''; }";
          style.appendChild(document.createTextNode(specificityWrapper));
          document.head.appendChild(style);

          // Load the Whitelabel CSS and wait for it
          const link = document.createElement("link");
          link.type = "text/css";
          link.rel = "stylesheet";
          link.href = whitelabelUrl;

          await new Promise<void>((resolve) => {
            link.onload = () => {
              document.getElementById("root")?.classList.add("whitelabel-active");
              resolve();
            };
            link.onerror = () => {
              console.warn("Whitelabel CSS failed to load. Proceeding without whitelabel styles.");
              resolve(); // IMPORTANT: Resolve on error to ensure the page loads
            }
            document.head.appendChild(link);
          });
        }

        // 2.5 if PWA, fetch manifest
        const currentHost = window.location.hostname;
        const manifestMap = config.manifestMap || [];
        let manifestURL = null;

        // check for the current host in the config's manifest map
        for (const [host, url] of Object.entries(manifestMap)) {
          if (currentHost.includes(host)) {
            manifestURL = url;
            break;
          }
        }

        if (manifestURL) {
          // make sure it exists by trying fetch it before assigning
          fetch(manifestURL, { method: "HEAD" }).then((manifestRes) => {
            if (manifestRes.ok) {
              // remove old manifest links if any
              document.querySelectorAll("link[rel='manifest']").forEach((e) => e.remove());
              const manifestLink = document.createElement("link");
              manifestLink.rel = "manifest";
              manifestLink.href = manifestURL;
              headID.appendChild(manifestLink);
              serviceWorkerRegistration.register({});
            }
          }).catch((err) => {
            console.error("Error fetching manifest:", err);
          });
        }

        // 3. Fetch Content (Essential Step, kept outside Promise.all for clarity)
        let content;
        const { language } = navigator;
        const langCode = `-${language.substring(0, 2)}`;
        const langContent = (url: string) => url.replace("content.json", `content${langCode}.json`);
        const contentUrl = config.content_url;

        const url = config.remote_prefix
          ? `${config.remote_prefix}/${hostPrefix}/content.json`
          : contentUrl;

        if (url) {
          const contentLang = langContent(url);
          content = await axios.get(contentLang).catch(async () => {
             console.log(`no content${langCode}.json file found. Falling back to default.`);
             return axios.get(url);
          });
        }
        
        // 4. Final Setup
        if (content) {
          window.config = { ...window.config, ...content.data };
          if (!content.data.pages) {
            console.log("content.json missing `pages` key");
          }
        }
        setSettings(window.config.theme?.settings);
        
        // Load per-project analytics config for PWA tracking
        const analyticsConfigUrl = window.config.remote_prefix
          ? `${window.config.remote_prefix}/${hostPrefix}/analytics-config.json`
          : contentUrl?.replace("content.json", "analytics-config.json");

        if (analyticsConfigUrl) {
          try {
            const analyticsResponse = await fetch(analyticsConfigUrl);
            if (analyticsResponse.ok) {
              const analyticsConfig = await analyticsResponse.json();
              window.config.analytics_config = analyticsConfig;
            }
          } catch {
            console.error("Failed to load analytics-config.json - PWA analytics disabled");
          }
        }

        // Call the Native PostMessage Service before everything else
        initializeNativePostMessageService();

        // Add Static Widget Configs, and OnboardingModules
        addConfig("StaticWidgets", [LocationSelectionConfig(), SearchBarConfig(), ProfileConfig(), RecoEngineConfig()]);
        addConfig("FlowModule", useFlows());

        // 4.5. Wait for i18n initialization to complete (prevents FUOC)
        await i18nInitPromise;

        // 5. Success: Mark Ready and Reveal Content
        setIsAppReady(true);
        if (rootElement) {
          rootElement.style.opacity = "1";
        }

      } catch (error) {
        // 6. Critical Failure: Handle Error & Force Reveal
        console.error("Critical error during app startup:", error);
        handleError({
          message: "App failed to load critical configuration.",
        });

        if (rootElement) {
          rootElement.style.opacity = "1";
        }
      }
    };

    startupActions();
  }, [setIsAppReady, handleError]);

  // Analytics
  useEffect(() => {
    if (window.config && !isAnalyticsInitialized) {
      setIsAnalyticsInitialized(true);
    }
  }, [isAnalyticsInitialized, setIsAnalyticsInitialized]);

  useOnboardingRedirect(configs);

  if (!isAppReady) {
    // If isAppReady is false, this runs, but the index.html opacity: 0 keeps content hidden.
    return <div />;
  }

  const noscriptText = i18n.t("noscript");

  return (
    <div className="App">
      <Helmet>
        <meta charSet={i18n.t("charset")} />
        <link href={i18n.t("favicon")} />
        <link rel="shortcut icon" href={window.config.favicon} />
        <noscript>{noscriptText.toString()}</noscript>
      </Helmet>
      <div className="app-wrapper">
        <PWAHeader />
        <PWAInstallPrompt />
        {/* removed <main> tag for accessibility purposes; re-add if needed */}
        <div className="main-content">
          <SlideRoutes timing="ease-in-out" duration={0}>
            {isAppReady && RouteCreationService.createListingPageRoute()}
            {isAppReady && RouteCreationService.createRoutes(configs.staticWidgets)}
          </SlideRoutes>
          {isAppReady &&
            window.config.flows &&
            RouteCreationService.createOnboardingRoutes(configs.flowModule.before, location, true)}
          {window.config.flows && ModalCreationService.createModals(configs.flowModule.after)}
        </div>
      </div>
    </div>
  );
};

export default App;